import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { interactive } from '@/components/ui/interactive';
import { cn } from '@/lib/utils';
import type { ComponentProps } from 'react';

/**
 * The record modal from the approved prototype, rebuilt on Radix Dialog.
 *
 * The prototype's Alpine version was a plain `x-show` div: no focus trap, no restore of
 * focus on close, no `aria-modal`, and Escape only worked because a `@keydown` happened
 * to be bound. Radix supplies all of that, and the visual result is identical — navy
 * scrim at 50% with a blur, a 2xl white panel, a bordered header, a scrolling body
 * capped at 70vh, and a sideline-tinted footer.
 *
 * `Dialog` mirrors shadcn/ui's API so a component added later with the CLI composes with
 * it; `DialogBody` is the one addition, because the prototype's scroll boundary sits
 * between the header and the footer rather than around the whole panel.
 */
function Dialog(props: ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger(props: ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogClose(props: ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-navy/50 p-4 backdrop-blur-sm',
        'data-[state=closed]:animate-overlay-out data-[state=open]:animate-overlay-in',
        className,
      )}
      {...props}
    />
  );
}

interface DialogContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  /** Accessible name for the close control — always a translated string. */
  closeLabel: string;
}

function DialogContent({ className, children, closeLabel, ...props }: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay>
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            'relative my-8 w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl outline-none',
            'data-[state=closed]:animate-panel-out data-[state=open]:animate-panel-in',
            className,
          )}
          {...props}
        >
          {children}
          <DialogPrimitive.Close
            aria-label={closeLabel}
            className={cn(
              interactive,
              'absolute end-6 top-4 grid h-8 w-8 place-items-center rounded-lg text-conc-400 hover:bg-sideline hover:text-navy',
            )}
          >
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPrimitive.Portal>
  );
}

function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('border-b border-conc-100 px-6 py-4 pe-16', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('truncate text-[16px] font-bold text-navy', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('truncate text-[12.5px] text-conc-500', className)}
      {...props}
    />
  );
}

function DialogBody({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-body"
      className={cn('max-h-[70vh] overflow-y-auto px-6 py-5', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-wrap items-center gap-2 border-t border-conc-100 bg-sideline px-6 py-4',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
};
